export enum HTTP_STATUS {
  BAD_REQUEST = 400,
  UNAUTHORIZED = 401,
  FORBIDDEN = 403,
  NOT_FOUND = 404,
  TEAPOT = 418,
}

/**
 * Defines specific error codes that can be returned by the API.
 */
export type ErrorCode = "requires_payment_information" | "jobs_subscription_required";
/**
 * Represents the standardized structure of an error returned by the API.
 */
export interface ApiErrorData {
  /** A unique code identifying the error type. */
  code: string;
  /** A human-readable message describing the error. */
  message: string;
  /** (Optional) The specific field or resource that the error pertains to. */
  target?: string | null;
  /** (Optional) The value of the target field that caused the error. */
  target_value?: string | null;
  /** (Optional) An array of nested `ApiErrorData` objects for detailed errors. */
  details?: Array<ApiErrorData>;
}

/**
 * A basic container for managing a standardized error returned from our APIs.
 * It can handle all known error formats returned from Fetch responses.
 *
 * @example
 * ```
 * import { ApiError } from "@sidegigllc/api";
 *
 * try {
 *     await useStafferUsersUpdate(...);
 * } catch (e) {
 *     const error = ApiError.fromThrown(e);
 *     console.error(error.message);
 * }
 * ```
 */
// oxlint-disable-next-line typescript/no-explicit-any -- unknown rejects the types assigned here
export class ApiError<T extends Record<string, any> = Record<string, any>> extends Error {
  httpStatus: number | undefined;
  httpMethod: string | undefined;
  url: string | undefined;
  /** How long the server asked us to wait (`Retry-After`), when it said so. */
  retryAfterMs: number | undefined;

  isUnknownError = () => this.error.code === ApiError.unknownCode;

  isBadRequest = () => this.httpStatus === HTTP_STATUS.BAD_REQUEST;
  isUnauthorized = () => this.httpStatus === HTTP_STATUS.UNAUTHORIZED;
  isForbidden = () => this.httpStatus === HTTP_STATUS.FORBIDDEN;
  isNotFound = () => this.httpStatus === HTTP_STATUS.NOT_FOUND;

  constructor(
    public error: ApiErrorData,
    public rawData: unknown,
    httpStatus?: number,
    httpMethod?: string,
    url?: string,
    // oxlint-disable-next-line unicorn/custom-error-definition -- changing Error.name alters error reporting
  ) {
    super(`Request failed with code ${error.code}: ${error.message}`);
    this.httpStatus = httpStatus;
    this.httpMethod = httpMethod;
    this.url = url;
  }

  getFieldErrorMessage(fieldName: keyof T): string | undefined {
    if (!this.hasFieldError(fieldName)) return;
    if (fieldName === this.error.target) return this.error.message;
    return this.error.details?.find((detail) => fieldName === detail.target)?.message;
  }

  getMainErrorMessage(): string {
    return this.error.message;
  }

  getMainErrorCode(): string {
    return this.error.code;
  }

  getDetails(): Array<ApiErrorData> | undefined {
    return this.error.details;
  }

  hasFieldError(fieldName: keyof T) {
    return fieldName === this.error.target || !!this.error.details?.some((detail) => fieldName === detail.target);
  }

  hasErrorCode(code: ErrorCode) {
    return this.error.code === code || !!this.error.details?.some((detail) => detail.code === code);
  }

  static readonly unknownCode = "unknown";
  // oxlint-disable-next-line typescript/no-explicit-any -- unknown rejects the types assigned here
  static unknown<T extends Record<string, any> = Record<string, never>>(httpStatus?: number): ApiError<T> {
    return new ApiError<T>({ code: ApiError.unknownCode, message: "Unknown error" }, undefined, httpStatus);
  }

  // oxlint-disable-next-line typescript/no-explicit-any -- unknown rejects the types assigned here
  static readonly fromThrown = <T extends Record<string, any>>(error: unknown): ApiError<T> => {
    if (error instanceof ApiError) return error;
    return ApiError.unknown<T>();
  };

  // oxlint-disable-next-line typescript/no-explicit-any -- unknown rejects the types assigned here
  static readonly fromResponseData = <T extends Record<string, any>>(
    data: unknown,
    httpStatus?: number,
    httpMethod?: string,
    url?: string,
  ): ApiError<T> => {
    // Bare JSON string body (e.g. admin Certifly-readonly 400) — preserve message + raw.
    if (typeof data === "string" && data && (httpStatus ?? 0) < 500) {
      return new ApiError({ code: "invalid", message: data }, data, httpStatus, httpMethod, url);
    }
    if (!data || typeof data !== "object") return ApiError.unknown<T>(httpStatus);
    if ((httpStatus ?? 0) >= 500) return ApiError.unknown<T>(httpStatus);

    // Standard shape: { error: { code, message } }
    // oxlint-disable-next-line typescript/no-explicit-any -- unknown rejects the types assigned here
    const errorObj = (data as any).error ?? data;
    if (errorObj?.code && errorObj.message) {
      return new ApiError(errorObj, data, httpStatus, httpMethod, url);
    }

    // Legacy shapes: flat object { field: [errors] }, array, { message }, { detail }, etc.
    // oxlint-disable-next-line typescript/no-explicit-any -- unknown rejects the types assigned here
    return convertLegacyError<T>(data as Record<string, any>, httpStatus, httpMethod, url);
  };
}

// oxlint-disable-next-line typescript/no-explicit-any -- unknown rejects the types assigned here
function convertLegacyError<T extends Record<string, any>>(
  // oxlint-disable-next-line typescript/no-explicit-any -- unknown rejects the types assigned here
  error: Record<string, any> | Array<string> | undefined,
  status?: number,
  method?: string,
  url?: string,
): ApiError<T> {
  try {
    if (!error || (status ?? 0) >= 500) return ApiError.unknown<T>(status);
    const code = "invalid";

    if (Array.isArray(error)) {
      // A DRF `many=True` body holds one entry per posted item, so `error[0]` is an object: neither a
      // message a consumer could render nor a `target` for `setFormErrors` to map.
      const first = typeof error[0] === "string" ? { message: error[0] } : flattenLegacyEntry("", error);
      return first ? new ApiError({ code, ...first }, error, status, method, url) : ApiError.unknown<T>(status);
    }

    const message: string | Array<string> | undefined = error.message ?? error.detail ?? error.reason ?? error;
    if (typeof message === "string" || Array.isArray(message)) {
      return new ApiError({ code, message: Array.isArray(message) ? message[0] : message }, error, status, method, url);
    }

    const keys = Object.keys(error);
    if (keys.length > 0) {
      const first = flattenLegacyEntry(keys[0], error[keys[0]]);
      if (first) {
        const errorData: Partial<ApiErrorData> = { code, message: first.message, target: first.target };
        for (const key of keys.slice(1)) {
          const detail = flattenLegacyEntry(key, error[key]);
          if (!detail) continue;
          errorData.details ??= [];
          errorData.details.push({ code, message: detail.message, target: detail.target });
        }
        return new ApiError(errorData as ApiErrorData, error, status, method, url);
      }
    }
  } catch (error_) {
    console.error("Failed to convert legacy error", error_); // oxlint-disable-line no-console
  }
  return ApiError.unknown<T>();
}

/** Walks a DRF-style nested error to find the first string leaf, preserving the deepest key as `target`. */
function flattenLegacyEntry(key: string, value: unknown): { target: string; message: string } | undefined {
  if (typeof value === "string") return { target: key, message: value };
  if (Array.isArray(value)) {
    for (const v of value) {
      const r = flattenLegacyEntry(key, v);
      if (r) return r;
    }
    return undefined;
  }
  if (value && typeof value === "object") {
    for (const [k, v] of Object.entries(value)) {
      const r = flattenLegacyEntry(k, v);
      if (r) return r;
    }
  }
  return undefined;
}

/** react-query `retry`: never retry a 403, otherwise retry up to 2 attempts. */
export const retryUnlessForbidden = (failureCount: number, error: unknown): boolean =>
  !ApiError.fromThrown(error).isForbidden() && failureCount < 2;

/**
 * Checks if a given error object represents a specific Stripe payment error.
 *
 * @param error - The error object to check. This can be an `ApiError`
 *                or a raw error object containing a `data` or `response.data` property.
 * @returns `true` if the error indicates a "PAYMENT_METHOD_DOES_NOT_EXIST" Stripe error, `false` otherwise.
 */
// oxlint-disable-next-line typescript/no-explicit-any -- unknown rejects the types assigned here
export const isStripePaymentError = (error: any): boolean => {
  const errorId =
    error?.response?.data?.id ?? error?.data?.body?.id ?? error?.data?.id ?? error?.rawData?.id ?? error?.id;
  return errorId === "PAYMENT_METHOD_DOES_NOT_EXIST";
};
